import { useState } from 'react';
import { Link } from 'react-router-dom';

interface CodeBlockProps {
  code: string;
}

function CodeBlock({ code }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group">
      <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm">
        <code>{code}</code>
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-2 bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity text-sm"
      >
        {copied ? '✓' : '📋'}
      </button>
    </div>
  );
}

interface AccordionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function Accordion({ title, children, defaultOpen = false }: AccordionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 rounded-lg mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 rounded-t-lg hover:bg-gray-100"
      >
        <span className="font-medium text-gray-900">{title}</span>
        <span>{isOpen ? '▼' : '▶'}</span>
      </button>
      {isOpen && <div className="p-4">{children}</div>}
    </div>
  );
}

export function DocsPage() {
  const [activeTab, setActiveTab] = useState<'getting-started' | 'testing' | 'api-reference' | 'mcp-setup'>('getting-started');

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <span className="text-3xl">📖</span>
          Documentation
        </h1>
        <p className="mt-2 text-gray-600">
          Guides and references for using the EDI Label Service.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex space-x-4" aria-label="Docs tabs">
          {[
            { id: 'getting-started' as const, label: 'Getting Started', icon: '🚀' },
            { id: 'testing' as const, label: 'Testing Labels', icon: '🏷️' },
            { id: 'api-reference' as const, label: 'API Reference', icon: '💻' },
            { id: 'mcp-setup' as const, label: 'MCP Setup', icon: '🔌' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`whitespace-nowrap pb-3 px-3 border-b-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-1">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'getting-started' && <GettingStartedContent />}
      {activeTab === 'testing' && <TestingLabelsContent CodeBlock={CodeBlock} />}
      {activeTab === 'api-reference' && <ApiReferenceContent />}
      {activeTab === 'mcp-setup' && <McpSetupContent CodeBlock={CodeBlock} Accordion={Accordion} />}
    </div>
  );
}

// ─── Getting Started Tab ────────────────────────────────

function GettingStartedContent() {
  return (
    <div className="space-y-8">
      <section>
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Welcome</h2>
        <p className="text-gray-600 mb-4">
          The EDI Label Service lets you generate ZPL shipping labels via API — from single labels and small
          batches (≤100) to async bulk jobs (up to 10,000) with ordered ZPL and optional PDF ZIP downloads.
          This guide walks you through setup and the main workflows.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Portal</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Link to="/" className="border rounded-lg p-4 hover:border-primary-300 hover:bg-primary-50 transition-colors block">
            <h3 className="font-medium flex items-center gap-2">📊 Dashboard</h3>
            <p className="text-sm text-gray-600 mt-1">Usage overview, stats, and recent activity.</p>
          </Link>
          <Link to="/api-keys" className="border rounded-lg p-4 hover:border-primary-300 hover:bg-primary-50 transition-colors block">
            <h3 className="font-medium flex items-center gap-2">🔑 API Keys</h3>
            <p className="text-sm text-gray-600 mt-1">View your API keys for authentication.</p>
          </Link>
          <Link to="/templates" className="border rounded-lg p-4 hover:border-primary-300 hover:bg-primary-50 transition-colors block">
            <h3 className="font-medium flex items-center gap-2">📄 Templates</h3>
            <p className="text-sm text-gray-600 mt-1">Browse assigned label templates and their field requirements.</p>
          </Link>
          <Link to="/test-label" className="border rounded-lg p-4 hover:border-primary-300 hover:bg-primary-50 transition-colors block">
            <h3 className="font-medium flex items-center gap-2">🏷️ Test Label</h3>
            <p className="text-sm text-gray-600 mt-1">Generate and preview test labels interactively.</p>
          </Link>
          <Link to="/usage" className="border rounded-lg p-4 hover:border-primary-300 hover:bg-primary-50 transition-colors block">
            <h3 className="font-medium flex items-center gap-2">📈 Usage</h3>
            <p className="text-sm text-gray-600 mt-1">Detailed usage statistics and trends.</p>
          </Link>
          <Link to="/docs" className="border rounded-lg p-4 hover:border-primary-300 hover:bg-primary-50 transition-colors block bg-primary-50 border-primary-200">
            <h3 className="font-medium flex items-center gap-2">📖 Docs</h3>
            <p className="text-sm text-gray-600 mt-1">This page — API reference and guides.</p>
          </Link>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Start Steps</h2>
        <ol className="space-y-4">
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-sm font-bold">1</span>
            <div>
              <p className="font-medium">Check your Templates</p>
              <p className="text-sm text-gray-600">Go to the Templates page to see which label templates are available to your account.</p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-sm font-bold">2</span>
            <div>
              <p className="font-medium">Test a Label</p>
              <p className="text-sm text-gray-600">Go to Test Label, select a template, fill in sample data, and generate a preview to verify it works.</p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-sm font-bold">3</span>
            <div>
              <p className="font-medium">Get your API Key</p>
              <p className="text-sm text-gray-600">Go to API Keys to find your key. Use it in the <code className="bg-gray-100 px-1 rounded">X-API-Key</code> header for all API calls.</p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-sm font-bold">4</span>
            <div>
              <p className="font-medium">Integrate</p>
              <p className="text-sm text-gray-600">Use the API Reference tab for detailed endpoint documentation and code examples in JavaScript, Python, and C#. For AI coding assistants, see the MCP Setup tab to connect Claude Code or Codex.</p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-sm font-bold">5</span>
            <div>
              <p className="font-medium">Bulk / warehouse waves (100–10,000 labels)</p>
              <p className="text-sm text-gray-600">
                Use <strong>async jobs</strong> — <code className="bg-gray-100 px-1 rounded">POST /labels/jobs</code> — not the sync batch endpoint.
                Full flow (create → upload gzip manifest → commit → poll → download ZPL / PDF ZIP) is under{' '}
                <strong>API Reference → Large-batch jobs</strong>.
              </p>
            </div>
          </li>
        </ol>
      </section>

      <section className="bg-amber-50 border border-amber-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Sync batch vs bulk jobs</h2>
        <div className="overflow-x-auto text-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="text-left text-xs uppercase text-gray-500">
                <th className="py-2 pr-4">Need</th>
                <th className="py-2 pr-4">API</th>
                <th className="py-2">Limit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 text-gray-700">
              <tr>
                <td className="py-2 pr-4">Interactive / small print</td>
                <td className="py-2 pr-4 font-mono text-xs">POST /labels/zpl or /labels/zpl/batch</td>
                <td className="py-2">≤100, request stays open</td>
              </tr>
              <tr>
                <td className="py-2 pr-4">Full ASN wave / roll archive</td>
                <td className="py-2 pr-4 font-mono text-xs">POST /labels/jobs (async)</td>
                <td className="py-2">Up to 10,000; poll for ZPL then optional PDF ZIP</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="bg-gray-50 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Need Help?</h2>
        <p className="text-gray-600 text-sm">
          Contact <a href="mailto:support@edi-partners.com" className="text-primary-600 hover:underline">support@edi-partners.com</a> for 
          integration assistance or custom template requests.
        </p>
      </section>
    </div>
  );
}

// ─── Testing Labels Tab ─────────────────────────────────

function TestingLabelsContent({ CodeBlock }: { CodeBlock: React.ComponentType<{ code: string }> }) {
  return (
    <div className="space-y-8">
      <section>
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Using the Test Label Page</h2>
        <p className="text-gray-600 mb-4">
          The Test Label page lets you interactively generate labels and see visual previews without writing code. 
          Use it to verify templates, check barcode placement, and debug field formatting.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Step-by-Step</h2>
        <ol className="space-y-3 text-sm text-gray-700">
          <li className="flex gap-2"><span className="font-bold text-primary-600">1.</span> Navigate to <strong>Test Label</strong> in the sidebar.</li>
          <li className="flex gap-2"><span className="font-bold text-primary-600">2.</span> Select a template using the cascading dropdowns (Industry → Company → Template).</li>
          <li className="flex gap-2"><span className="font-bold text-primary-600">3.</span> Edit the JSON payload — include all required fields for the selected template.</li>
          <li className="flex gap-2"><span className="font-bold text-primary-600">4.</span> Click <strong>"Generate ZPL"</strong> to produce raw ZPL code.</li>
          <li className="flex gap-2"><span className="font-bold text-primary-600">5.</span> Click <strong>"Preview"</strong> to render a visual image of the label.</li>
        </ol>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Sample Payloads</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-900 mb-2">Shipping Label</h3>
            <CodeBlock code={`{
  "shipFrom": {
    "name": "Acme Corp Warehouse",
    "address1": "123 Industrial Blvd",
    "city": "Austin",
    "state": "TX",
    "postalCode": "78701"
  },
  "shipTo": {
    "name": "Walmart Store #4215",
    "address1": "500 Retail Drive",
    "city": "Dallas",
    "state": "TX",
    "postalCode": "75201"
  },
  "poNumber": "PO-2026-789012",
  "sscc": "007468510001263940",
  "cartonNumber": 1,
  "cartonTotal": 10
}`} />
          </div>

          <div>
            <h3 className="font-medium text-gray-900 mb-2">Carton Label</h3>
            <CodeBlock code={`{
  "sscc": "007468510001263941",
  "upc": "012345678905",
  "itemNumber": "ITEM-001",
  "itemDescription": "Widget - Blue, Large",
  "cartonQty": 24,
  "poNumber": "PO-2026-789012"
}`} />
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Testing Tips</h2>
        <ul className="space-y-2 text-sm text-gray-600">
          <li className="flex gap-2">💡 <span>Start with the default payload — it has common fields that work with most templates.</span></li>
          <li className="flex gap-2">💡 <span>Modify one field at a time to isolate issues.</span></li>
          <li className="flex gap-2">💡 <span>Test required fields only first, then add optional ones.</span></li>
          <li className="flex gap-2">💡 <span>If you already have a multi-label <code className="bg-gray-100 px-1 rounded">.zpl</code> file, use <code className="bg-gray-100 px-1 rounded">POST /labels/zpl/preview</code> to download a merged PDF — see the API Reference tab.</span></li>
          <li className="flex gap-2">💡 <span>If preview looks wrong, copy the ZPL and test at <a href="https://labelary.com/viewer.html" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">labelary.com</a>, or upload the file to our <code className="bg-gray-100 px-1 rounded">/labels/zpl/preview</code> endpoint.</span></li>
          <li className="flex gap-2">💡 <span>Test edge cases: long addresses, special characters, max-length fields.</span></li>
        </ul>
      </section>
    </div>
  );
}

// ─── API Reference Tab (original content) ───────────────

function ApiReferenceContent() {
  return (
    <>
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Quick Start</h2>
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-4">
          <p className="text-primary-800">
            <strong>Base URL:</strong> <code className="bg-primary-100 px-2 py-1 rounded">https://labelapi.edi-partners.com/api/v1</code>
          </p>
        </div>
        
        <p className="text-gray-600 mb-4">
          All API requests require authentication via the <code className="bg-gray-100 px-2 py-1 rounded">X-API-Key</code> header.
        </p>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-sm text-blue-900">
          <p className="font-medium mb-2">Which label API should I use?</p>
          <ul className="space-y-1 list-disc list-inside">
            <li><code className="bg-blue-100 px-1 rounded">POST /labels/zpl</code> — single label, synchronous</li>
            <li><code className="bg-blue-100 px-1 rounded">POST /labels/zpl/batch</code> — up to <strong>100</strong> labels, synchronous (optional PDF via <code className="bg-blue-100 px-1 rounded">Accept</code>)</li>
            <li><code className="bg-blue-100 px-1 rounded">POST /labels/jobs</code> — <strong>async bulk</strong> up to <strong>10,000</strong> labels (ZPL first, optional PDF ZIP) — see Large-Batch Jobs below</li>
            <li><code className="bg-blue-100 px-1 rounded">POST /labels/zpl/preview</code> — you already have ZPL; upload for PDF/PNG preview</li>
            <li><code className="bg-blue-100 px-1 rounded">POST /labels/preview</code> — single-label visual preview from JSON (testing only)</li>
          </ul>
        </div>

        <CodeBlock code={`curl -X POST https://labelapi.edi-partners.com/api/v1/labels/zpl \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "templateKey": "bucees-shipping-4x6",
    "data": {
      "shipFrom": { "name": "Warehouse", "address1": "123 Main St", "city": "Austin", "state": "TX", "postalCode": "78701" },
      "shipTo": { "name": "Store #1", "address1": "456 Oak Ave", "city": "Houston", "state": "TX", "postalCode": "77001" },
      "poNumber": "PO123456",
      "sscc": "007468510001263940",
      "cartonNumber": 1,
      "cartonTotal": 1
    }
  }'`} />
      </section>

      {/* Endpoints */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <span>💻</span>
          API Endpoints
        </h2>

        <Accordion title="GET /api/v1/templates - List Available Templates" defaultOpen>
          <p className="text-gray-600 mb-4">Returns all label templates available to your tenant.</p>
          <h4 className="font-medium text-gray-900 mb-2">Response</h4>
          <CodeBlock code={`[
  {
    "templateKey": "bucees-shipping-4x6",
    "retailer": "buc-ees",
    "labelType": "shipping",
    "version": "1.0.0",
    "status": "active",
    "description": "Buc-ee's Shipping Label"
  }
]`} />
        </Accordion>

        <Accordion title="POST /api/v1/labels/zpl - Generate ZPL Label">
          <p className="text-gray-600 mb-4">Generate a single ZPL label from a template.</p>
          <h4 className="font-medium text-gray-900 mb-2">Request Body</h4>
          <CodeBlock code={`{
  "templateKey": "bucees-shipping-4x6",
  "data": {
    "shipFrom": {
      "name": "ABC Distribution",
      "address1": "123 Warehouse Blvd",
      "city": "Austin",
      "state": "TX",
      "postalCode": "78701"
    },
    "shipTo": {
      "name": "Buc-ees Store #17",
      "locationNumber": "0017",
      "address1": "10070 W IH-10",
      "city": "Luling",
      "state": "TX",
      "postalCode": "78648"
    },
    "poNumber": "001725110288",
    "sscc": "007468510001263940",
    "cartonNumber": 1,
    "cartonTotal": 5
  }
}`} />
          <h4 className="font-medium text-gray-900 mt-4 mb-2">Query Parameters</h4>
          <p className="text-gray-600 mb-2">
            <code className="bg-gray-100 px-1 rounded">outputMode</code> (optional): Set to <code className="bg-gray-100 px-1 rounded">printer-optimized</code> to
            use the ^DFR/^XFR stored-format pattern, which reduces data sent to the printer by storing the template once in printer memory. Default: <code className="bg-gray-100 px-1 rounded">standard</code>.
          </p>
          <h4 className="font-medium text-gray-900 mt-4 mb-2">Response</h4>
          <CodeBlock code={`{
  "zpl": "^XA\\n^MCY\\n^XZ\\n...",
  "templateKey": "bucees-shipping-4x6",
  "templateVersion": "1.0.0",
  "requestId": "abc123-def456",
  "warnings": []
}`} />
        </Accordion>

        <Accordion title="POST /api/v1/labels/zpl/batch - Generate Multiple Labels">
          <p className="text-gray-600 mb-4">
            Generate up to 100 labels in a single request. Each item in the <code className="bg-gray-100 px-1 rounded">labels</code> array
            is a full label request (same shape as the single-label endpoint). The batch can mix different templates.
          </p>

          <h4 className="font-medium text-gray-900 mb-2">Request Body</h4>
          <CodeBlock code={`{
  "labels": [
    {
      "templateKey": "bucees-shipping-4x6",
      "data": {
        "shipFrom": { "name": "ABC Distribution", "address1": "123 Warehouse Blvd", "city": "Austin", "state": "TX", "postalCode": "78701" },
        "shipTo": { "name": "Buc-ees Store #17", "address1": "10070 W IH-10", "city": "Luling", "state": "TX", "postalCode": "78648" },
        "poNumber": "001725110288",
        "sscc": "007468510001263940",
        "cartonNumber": 1,
        "cartonTotal": 5
      }
    },
    {
      "templateKey": "bucees-shipping-4x6",
      "data": {
        "shipFrom": { "name": "ABC Distribution", "address1": "123 Warehouse Blvd", "city": "Austin", "state": "TX", "postalCode": "78701" },
        "shipTo": { "name": "Buc-ees Store #17", "address1": "10070 W IH-10", "city": "Luling", "state": "TX", "postalCode": "78648" },
        "poNumber": "001725110288",
        "sscc": "007468510001263941",
        "cartonNumber": 2,
        "cartonTotal": 5
      }
    }
  ],
  "outputMode": "standard"
}`} />

          <h4 className="font-medium text-gray-900 mt-4 mb-2">Options</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Field</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Default</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr><td className="px-4 py-2 font-mono text-sm">labels</td><td className="px-4 py-2">&mdash;</td><td className="px-4 py-2">Array of label requests (1&ndash;100). Each item has the same schema as a single-label request.</td></tr>
                <tr><td className="px-4 py-2 font-mono text-sm">outputMode</td><td className="px-4 py-2">standard</td><td className="px-4 py-2"><code className="bg-gray-100 px-1 rounded">standard</code> or <code className="bg-gray-100 px-1 rounded">printer-optimized</code>. Printer-optimized uses ^DFR/^XFR stored-format pattern to reduce data when all labels share the same template.</td></tr>
              </tbody>
            </table>
          </div>

          <h4 className="font-medium text-gray-900 mt-4 mb-2">Response (JSON)</h4>
          <CodeBlock code={`{
  "results": [
    {
      "index": 0,
      "success": true,
      "data": {
        "zpl": "^XA\\n^MCY\\n...",
        "templateKey": "bucees-shipping-4x6",
        "templateVersion": "1.0.0",
        "requestId": "a1b2c3d4-...",
        "warnings": []
      }
    },
    {
      "index": 1,
      "success": true,
      "data": { "zpl": "^XA\\n...", "templateKey": "bucees-shipping-4x6", "templateVersion": "1.0.0", "requestId": "e5f6g7h8-...", "warnings": [] }
    }
  ],
  "summary": {
    "total": 2,
    "successful": 2,
    "failed": 0
  }
}`} />

          <h4 className="font-medium text-gray-900 mt-4 mb-2">Content Negotiation</h4>
          <p className="text-gray-600 mb-2">
            Use the <code className="bg-gray-100 px-1 rounded">Accept</code> header to choose the response format.
            The default JSON response is returned when no Accept header or <code className="bg-gray-100 px-1 rounded">Accept: application/json</code> is used.
          </p>
          <div className="overflow-x-auto mb-4">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Accept header</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Response</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr><td className="px-4 py-2 font-mono text-sm">application/json</td><td className="px-4 py-2">JSON with per-label results and summary (default)</td></tr>
                <tr><td className="px-4 py-2 font-mono text-sm">text/plain</td><td className="px-4 py-2">Raw ZPL — all successful labels concatenated, ready for a printer</td></tr>
                <tr><td className="px-4 py-2 font-mono text-sm">application/pdf</td><td className="px-4 py-2">Single multi-page PDF of all successful labels (preview must be enabled)</td></tr>
                <tr><td className="px-4 py-2 font-mono text-sm">image/png</td><td className="px-4 py-2">ZIP archive with one PNG per successful label (<code className="bg-gray-100 px-1 rounded">label-01.png</code>, …). Response includes <code className="bg-gray-100 px-1 rounded">X-Label-Count</code> header.</td></tr>
              </tbody>
            </table>
          </div>
          <p className="text-amber-600 text-sm mb-4">
            PDF and PNG batch downloads require the preview feature to be enabled in your environment. Both formats use the same chunked PDF rendering path (50 labels per Labelary request); PNG batch converts each PDF page to an image locally before zipping.
          </p>
          <CodeBlock code={`# Raw ZPL
curl -X POST https://labelapi.edi-partners.com/api/v1/labels/zpl/batch \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -H "Accept: text/plain" \\
  -d '{"labels": [...]}' > batch-labels.zpl

# Multi-page PDF (all labels in one file)
curl -X POST https://labelapi.edi-partners.com/api/v1/labels/zpl/batch \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -H "Accept: application/pdf" \\
  -d '{"labels": [...]}' > batch-labels.pdf

# PNG previews (zip archive)
curl -X POST https://labelapi.edi-partners.com/api/v1/labels/zpl/batch \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -H "Accept: image/png" \\
  -d '{"labels": [...]}' > batch-labels.zip`} />
        </Accordion>

        <Accordion title="POST /api/v1/labels/jobs — Large-batch jobs (async, max 10,000 labels)" defaultOpen>
          <p className="text-gray-600 mb-4">
            Use this when you need <strong>more than 100 labels</strong>. You do <strong>not</strong> send all labels
            in one sync HTTP body. You create a job, upload a gzip file of label requests, commit, then poll (or get a webhook).
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-950">
            <p className="font-medium mb-1">Hard limit: 10,000 labels per job</p>
            <p>
              The API enforces <code className="bg-amber-100 px-1 rounded">labelCount</code> between <strong>1 and 10,000</strong>.
              That is a hard maximum (not a soft recommendation). Larger waves should be split into multiple jobs, or contact
              support if you need a product change to raise the cap.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 text-sm text-green-900">
            <p className="font-medium mb-1">What you get</p>
            <ul className="list-disc list-inside space-y-1">
              <li>ZPL always first (thermal print); optional PDF ZIP for archive / roll-sized multi-page PDFs</li>
              <li>
                PDF parts packaged as <code className="bg-green-100 px-1 rounded">labels-pdf.zip</code>
                {' '}(e.g. <strong>1000 labels per PDF</strong> on production canary for full label rolls)
              </li>
              <li>Typical wall time: a few minutes for large waves (PDF stage is the slow part)</li>
              <li>Artifacts kept <strong>7 days</strong>; download URLs are short-lived SAS links</li>
              <li>One active job with more than 100 labels per tenant at a time</li>
            </ul>
          </div>

          <h4 className="font-medium text-gray-900 mb-2">How to request (summary)</h4>
          <ol className="list-decimal list-inside text-sm text-gray-700 mb-4 space-y-1">
            <li>Build <code className="bg-gray-100 px-1 rounded">manifest.ndjson.gz</code> offline (one JSON line per label)</li>
            <li><code className="bg-gray-100 px-1 rounded">POST /labels/jobs</code> with metadata + <code className="bg-gray-100 px-1 rounded">Idempotency-Key</code></li>
            <li><code className="bg-gray-100 px-1 rounded">PUT</code> the gzip to <code className="bg-gray-100 px-1 rounded">manifestUploadUrl</code> (Azure Blob SAS)</li>
            <li><code className="bg-gray-100 px-1 rounded">POST /labels/jobs/&#123;jobId&#125;/commit</code></li>
            <li>Poll status <strong>or</strong> wait for webhook / email</li>
            <li>Download ZPL / PDF ZIP from <code className="bg-gray-100 px-1 rounded">artifacts[]</code></li>
          </ol>

          <h4 className="font-medium text-gray-900 mb-2">Endpoints</h4>
          <div className="overflow-x-auto mb-4 text-sm">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Path</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Purpose</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 text-gray-700">
                <tr>
                  <td className="px-3 py-2 font-mono">POST</td>
                  <td className="px-3 py-2 font-mono text-xs">/labels/jobs</td>
                  <td className="px-3 py-2">Create job; returns manifest upload SAS</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-mono">PUT</td>
                  <td className="px-3 py-2 font-mono text-xs">manifestUploadUrl (Blob SAS)</td>
                  <td className="px-3 py-2">Upload gzip NDJSON (not our API host)</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-mono">POST</td>
                  <td className="px-3 py-2 font-mono text-xs">/labels/jobs/&#123;jobId&#125;/commit</td>
                  <td className="px-3 py-2">Verify hash and start pipeline</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-mono">GET</td>
                  <td className="px-3 py-2 font-mono text-xs">/labels/jobs/&#123;jobId&#125;</td>
                  <td className="px-3 py-2">Status + artifact SAS (refresh by re-GET)</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-mono">POST</td>
                  <td className="px-3 py-2 font-mono text-xs">/labels/jobs/&#123;jobId&#125;/cancel</td>
                  <td className="px-3 py-2">Cooperative cancel</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-mono">POST</td>
                  <td className="px-3 py-2 font-mono text-xs">/labels/jobs/&#123;jobId&#125;/retry</td>
                  <td className="px-3 py-2">New jobId (optional reuseManifest)</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-mono">POST</td>
                  <td className="px-3 py-2 font-mono text-xs">/labels/jobs/&#123;jobId&#125;/upload-sas</td>
                  <td className="px-3 py-2">Renew write SAS while still upload_pending</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h4 className="font-medium text-gray-900 mb-2">Create job</h4>
          <p className="text-sm text-gray-600 mb-2">
            Required header: <code className="bg-gray-100 px-1 rounded">Idempotency-Key</code> (safe client retries).
            Body fields:
          </p>
          <div className="overflow-x-auto mb-3 text-sm">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Field</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rules</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 text-gray-700">
                <tr><td className="px-3 py-2 font-mono text-xs">labelCount</td><td className="px-3 py-2">1…10,000</td></tr>
                <tr><td className="px-3 py-2 font-mono text-xs">outputs</td><td className="px-3 py-2">Must include <code className="bg-gray-100 px-1 rounded">&quot;zpl&quot;</code>; add <code className="bg-gray-100 px-1 rounded">&quot;pdf&quot;</code> for PDF ZIP</td></tr>
                <tr><td className="px-3 py-2 font-mono text-xs">expandedBytes</td><td className="px-3 py-2">Upper bound of gunzipped NDJSON size (server max 128 MiB)</td></tr>
                <tr><td className="px-3 py-2 font-mono text-xs">manifestSha256</td><td className="px-3 py-2">64-char hex SHA-256 of the <strong>gzip file</strong> you will upload</td></tr>
              </tbody>
            </table>
          </div>
          <CodeBlock code={`curl -X POST https://labelapi.edi-partners.com/api/v1/labels/jobs \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Idempotency-Key: $(uuidgen)" \\
  -H "Content-Type: application/json" \\
  -d '{
    "labelCount": 1000,
    "outputs": ["zpl", "pdf"],
    "expandedBytes": 800000,
    "manifestSha256": "<sha256-hex-of-manifest.ndjson.gz>",
    "labelsPerRoll": 1000,
    "webhookUrl": "https://your-wms.example/hooks/edi-labels",
    "notifyEmail": "ops@yourcompany.com"
  }'`} />
          <p className="text-sm text-gray-600 mt-2 mb-4">
            Response <code className="bg-gray-100 px-1 rounded">202</code> includes <code className="bg-gray-100 px-1 rounded">jobId</code>,
            <code className="bg-gray-100 px-1 rounded">manifestUploadUrl</code>, <code className="bg-gray-100 px-1 rounded">commitUrl</code>, and{' '}
            <code className="bg-gray-100 px-1 rounded">statusUrl</code>.
            Optional: <code className="bg-gray-100 px-1 rounded">labelsPerRoll</code> (default <strong>1000</strong> — PDF/ZPL roll size),
            <code className="bg-gray-100 px-1 rounded">webhookUrl</code>, <code className="bg-gray-100 px-1 rounded">notifyEmail</code>.
            Server render part size is ops-configured (default 250 labels/part) and independent of roll size.
          </p>

          <h4 className="font-medium text-gray-900 mt-4 mb-2">Manifest (gzip NDJSON)</h4>
          <p className="text-sm text-gray-600 mb-2">
            One JSON object per line, indices contiguous <code className="bg-gray-100 px-1 rounded">0 … labelCount-1</code>.
            Each <code className="bg-gray-100 px-1 rounded">request</code> matches single-label generation (
            <code className="bg-gray-100 px-1 rounded">templateKey</code> + <code className="bg-gray-100 px-1 rounded">data</code>).
            Max compressed <strong>32 MiB</strong>; expanded <strong>128 MiB</strong>; ~64 KiB data per label.
          </p>
          <CodeBlock code={`{"index":0,"request":{"templateKey":"retail--natural-grocer--shipping-4x6","data":{…}}}
{"index":1,"request":{"templateKey":"retail--natural-grocer--shipping-4x6","data":{…}}}`} />

          <h4 className="font-medium text-gray-900 mt-4 mb-2">Upload + commit</h4>
          <CodeBlock code={`# Upload gzip to the SAS URL from create (Azure Blob — not the label API host)
curl -X PUT "$MANIFEST_UPLOAD_URL" \\
  -H "x-ms-blob-type: BlockBlob" \\
  -H "Content-Type: application/gzip" \\
  --data-binary @manifest.ndjson.gz

# Commit (same sha256 as create)
curl -X POST https://labelapi.edi-partners.com/api/v1/labels/jobs/$JOB_ID/commit \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"manifestSha256":"<same-sha256-hex>"}'`} />

          <h4 className="font-medium text-gray-900 mt-4 mb-2">Status &amp; downloads</h4>
          <p className="text-sm text-gray-600 mb-2">
            Poll every ~2 seconds (back off to 5–15s while running). When state is{' '}
            <code className="bg-gray-100 px-1 rounded">zpl-ready</code>, download ZPL for thermal print while PDF continues.
            Terminal states: <code className="bg-gray-100 px-1 rounded">completed</code>,{' '}
            <code className="bg-gray-100 px-1 rounded">completed_with_errors</code>,{' '}
            <code className="bg-gray-100 px-1 rounded">failed</code>, <code className="bg-gray-100 px-1 rounded">cancelled</code>.
          </p>
          <div className="overflow-x-auto mb-3 text-sm">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">State</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Meaning</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 text-gray-700">
                <tr><td className="px-3 py-2 font-mono text-xs">upload_pending</td><td className="px-3 py-2">Waiting for manifest PUT + commit</td></tr>
                <tr><td className="px-3 py-2 font-mono text-xs">validating</td><td className="px-3 py-2">Hash / stream checks</td></tr>
                <tr><td className="px-3 py-2 font-mono text-xs">generating-zpl</td><td className="px-3 py-2">Chunked ZPL generation</td></tr>
                <tr><td className="px-3 py-2 font-mono text-xs">zpl-ready</td><td className="px-3 py-2"><strong>Ordered ZPL available</strong> (PDF may still run)</td></tr>
                <tr><td className="px-3 py-2 font-mono text-xs">rendering</td><td className="px-3 py-2">PDF parts + ZIP</td></tr>
                <tr><td className="px-3 py-2 font-mono text-xs">completed</td><td className="px-3 py-2">All requested outputs ready</td></tr>
              </tbody>
            </table>
          </div>
          <CodeBlock code={`curl -H "X-API-Key: YOUR_API_KEY" \\
  https://labelapi.edi-partners.com/api/v1/labels/jobs/$JOB_ID

# Example terminal body:
{
  "jobId": "…",
  "state": "completed",
  "total": 1000,
  "succeeded": 1000,
  "failed": 0,
  "artifacts": [
    { "kind": "zpl", "url": "https://…/labels.zpl?<SAS>", "expiresAt": "…" },
    { "kind": "pdf-zip", "url": "https://…/labels-pdf.zip?<SAS>", "expiresAt": "…" }
  ]
}`} />
          <p className="text-sm text-gray-600 mb-2">
            Artifact kinds: <code className="bg-gray-100 px-1 rounded">zpl</code> (ordered multi-label ZPL),
            <code className="bg-gray-100 px-1 rounded">pdf-zip</code> (ZIP of multi-page PDF parts, e.g. 1000 pages each for roll-sized print),
            optional <code className="bg-gray-100 px-1 rounded">errors</code> NDJSON. GET the SAS URL directly (no API key on the blob request).
            Re-GET status to refresh SAS near expiry. Treat SAS URLs as secrets.
          </p>

          <h4 className="font-medium text-gray-900 mt-4 mb-2">Notifications (webhook + email)</h4>
          <p className="text-sm text-gray-600 mb-2">
            Optional on create: <code className="bg-gray-100 px-1 rounded">webhookUrl</code> (https only) and/or{' '}
            <code className="bg-gray-100 px-1 rounded">notifyEmail</code>. You can still poll; notifications are extra.
          </p>
          <ul className="text-sm text-gray-700 list-disc list-inside mb-2 space-y-1">
            <li><code className="bg-gray-100 px-1 rounded">label_job.zpl_ready</code> — ZPL downloadable (PDF may still run)</li>
            <li><code className="bg-gray-100 px-1 rounded">label_job.terminal</code> — completed / completed_with_errors / failed / cancelled</li>
          </ul>
          <CodeBlock code={`// Webhook body example (server → your HTTPS endpoint)
{
  "event": "label_job.terminal",
  "jobId": "…",
  "tenantId": "…",
  "state": "completed",
  "total": 1000,
  "succeeded": 1000,
  "failed": 0,
  "statusUrl": "https://labelapi.edi-partners.com/api/v1/labels/jobs/…",
  "artifacts": [
    { "kind": "zpl", "url": "https://…?<SAS>", "expiresAt": "…" },
    { "kind": "pdf-zip", "url": "https://…?<SAS>", "expiresAt": "…" }
  ],
  "occurredAt": "…"
}`} />

          <h4 className="font-medium text-gray-900 mt-4 mb-2">Full Node.js example</h4>
          <CodeBlock code={`import { createHash, randomUUID } from 'node:crypto';
import { gzipSync } from 'node:zlib';

const BASE = 'https://labelapi.edi-partners.com/api/v1';
const API_KEY = process.env.LABEL_API_KEY;

// Build NDJSON lines: { index, request: { templateKey, data } }
const labels = [/* your label requests */];
const lines = labels.map((request, index) => JSON.stringify({ index, request }));
const expanded = Buffer.from(lines.join('\\n') + '\\n', 'utf8');
const gzip = gzipSync(expanded);
const sha256 = createHash('sha256').update(gzip).digest('hex');

const createRes = await fetch(BASE + '/labels/jobs', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': API_KEY,
    'Idempotency-Key': randomUUID(),
  },
  body: JSON.stringify({
    labelCount: labels.length, // hard max 10000
    outputs: ['zpl', 'pdf'],
    expandedBytes: expanded.length,
    manifestSha256: sha256,
    labelsPerRoll: 1000, // warehouse print unit (default 1000)
    webhookUrl: 'https://your-wms.example/hooks/edi-labels', // optional
    notifyEmail: 'ops@yourcompany.com', // optional
  }),
});
const receipt = await createRes.json(); // jobId, manifestUploadUrl, commitUrl, statusUrl

await fetch(receipt.manifestUploadUrl, {
  method: 'PUT',
  headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': 'application/gzip' },
  body: gzip,
});

await fetch(receipt.commitUrl, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY },
  body: JSON.stringify({ manifestSha256: sha256 }),
});

// Poll until terminal (or rely on webhook)
let status;
for (;;) {
  await new Promise((r) => setTimeout(r, 3000));
  status = await (await fetch(receipt.statusUrl, {
    headers: { 'X-API-Key': API_KEY },
  })).json();
  if (['completed','completed_with_errors','failed','cancelled'].includes(status.state)) break;
}
// status.artifacts[].url → download ZPL / PDF ZIP (SAS; re-GET status if expired)`} />

          <h4 className="font-medium text-gray-900 mt-4 mb-2">MCP / AI assistants</h4>
          <p className="text-sm text-gray-600 mb-2">
            The MCP server (see <strong>MCP Setup</strong> tab) exposes{' '}
            <code className="bg-gray-100 px-1 rounded">generate-large-batch-labels</code> with the same
            <code className="bg-gray-100 px-1 rounded">labelsPerRoll</code> (default 1000), webhook, and email fields.
            Use sync <code className="bg-gray-100 px-1 rounded">generate-batch-labels</code> only for ≤100 labels.
          </p>
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
            Large-batch is enabled per environment and may be limited to specific tenants during rollout.
            If create returns <strong>503</strong> (feature off) or <strong>403</strong> (tenant not on allow-list), contact support to enable bulk for your account.
          </p>
        </Accordion>

        <Accordion title="POST /api/v1/labels/zpl/preview - Render Existing ZPL to PDF/PNG">
          <p className="text-gray-600 mb-4">
            Upload an existing ZPL file (single or multi-label) and receive a visual preview without regenerating labels from JSON.
            Use this when your WMS or another system already produced the ZPL — for example, a 78-carton download saved as a <code className="bg-gray-100 px-1 rounded">.zpl</code> file.
          </p>

          <h4 className="font-medium text-gray-900 mb-2">When to use this vs. batch</h4>
          <div className="overflow-x-auto mb-4">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Scenario</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Endpoint</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr><td className="px-4 py-2">You have JSON order data and need labels + PDF</td><td className="px-4 py-2 font-mono text-sm">POST /labels/zpl/batch + Accept: application/pdf</td></tr>
                <tr><td className="px-4 py-2">You already have a .zpl file and need a PDF</td><td className="px-4 py-2 font-mono text-sm">POST /labels/zpl/preview + Accept: application/pdf</td></tr>
                <tr><td className="px-4 py-2">You already have ZPL and need PNG images</td><td className="px-4 py-2 font-mono text-sm">POST /labels/zpl/preview + Accept: image/png</td></tr>
              </tbody>
            </table>
          </div>

          <h4 className="font-medium text-gray-900 mb-2">Request</h4>
          <p className="text-gray-600 mb-2">
            Send the raw ZPL as the request body. Each <code className="bg-gray-100 px-1 rounded">^XA … ^XZ</code> block is treated as one label.
            Label dimensions are read from <code className="bg-gray-100 px-1 rounded">^PW</code> and <code className="bg-gray-100 px-1 rounded">^LL</code> when present (defaults to 4&times;6 at 203 DPI).
          </p>
          <CodeBlock code={`POST /api/v1/labels/zpl/preview
Content-Type: text/plain
Accept: application/pdf
X-API-Key: YOUR_API_KEY

^XA
^PW812
^LL1218
... label content ...
^XZ
^XA
^PW812
^LL1218
... next label ...
^XZ`} />

          <h4 className="font-medium text-gray-900 mt-4 mb-2">Request headers</h4>
          <div className="overflow-x-auto mb-4">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Header</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Required</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr><td className="px-4 py-2 font-mono text-sm">X-API-Key</td><td className="px-4 py-2">Yes</td><td className="px-4 py-2">Your tenant API key</td></tr>
                <tr><td className="px-4 py-2 font-mono text-sm">Content-Type</td><td className="px-4 py-2">Recommended</td><td className="px-4 py-2"><code className="bg-gray-100 px-1 rounded">text/plain</code>, <code className="bg-gray-100 px-1 rounded">application/vnd.zebra-zpl</code>, or <code className="bg-gray-100 px-1 rounded">application/octet-stream</code></td></tr>
                <tr><td className="px-4 py-2 font-mono text-sm">Accept</td><td className="px-4 py-2">Optional</td><td className="px-4 py-2"><code className="bg-gray-100 px-1 rounded">application/pdf</code> (default) or <code className="bg-gray-100 px-1 rounded">image/png</code></td></tr>
              </tbody>
            </table>
          </div>

          <h4 className="font-medium text-gray-900 mt-4 mb-2">Response</h4>
          <div className="overflow-x-auto mb-4">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Accept header</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Content-Type</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr><td className="px-4 py-2 font-mono text-sm">application/pdf</td><td className="px-4 py-2 font-mono text-sm">application/pdf</td><td className="px-4 py-2">Single multi-page PDF — one page per label</td></tr>
                <tr><td className="px-4 py-2 font-mono text-sm">image/png</td><td className="px-4 py-2 font-mono text-sm">application/zip</td><td className="px-4 py-2">ZIP with <code className="bg-gray-100 px-1 rounded">label-01.png</code>, … Includes <code className="bg-gray-100 px-1 rounded">X-Label-Count</code> header.</td></tr>
              </tbody>
            </table>
          </div>

          <h4 className="font-medium text-gray-900 mt-4 mb-2">Limits</h4>
          <ul className="text-sm text-gray-600 mb-4 space-y-1 list-disc list-inside">
            <li>Maximum <strong>100 labels</strong> per request (same as batch)</li>
            <li>Maximum body size <strong>512 KB</strong></li>
            <li>Large files are rendered in chunks of 50 labels to comply with Labelary limits — e.g. 78 labels uses 2 upstream render calls, returned as one PDF</li>
            <li>Requires preview to be enabled in your environment (<code className="bg-gray-100 px-1 rounded">ENABLE_PREVIEW_ENDPOINT=true</code>)</li>
          </ul>

          <p className="text-amber-600 text-sm mb-4">
            This endpoint renders previews only — it does not validate template fields or regenerate ZPL. For production label generation, continue using <code className="bg-gray-100 px-1 rounded">POST /labels/zpl</code> or <code className="bg-gray-100 px-1 rounded">/labels/zpl/batch</code>.
          </p>

          <h4 className="font-medium text-gray-900 mb-2">Examples</h4>
          <CodeBlock code={`# 78-label file → single PDF (Windows)
curl.exe -X POST https://labelapi.edi-partners.com/api/v1/labels/zpl/preview ^
  -H "X-API-Key: YOUR_API_KEY" ^
  -H "Content-Type: text/plain" ^
  -H "Accept: application/pdf" ^
  --data-binary "@labels-289271872.zpl" ^
  -o labels.pdf

# Linux / macOS
curl -X POST https://labelapi.edi-partners.com/api/v1/labels/zpl/preview \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: text/plain" \\
  -H "Accept: application/pdf" \\
  --data-binary "@labels-289271872.zpl" \\
  -o labels.pdf

# PNG previews as a zip archive
curl -X POST https://labelapi.edi-partners.com/api/v1/labels/zpl/preview \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: text/plain" \\
  -H "Accept: image/png" \\
  --data-binary "@labels-289271872.zpl" \\
  -o labels.zip`} />
        </Accordion>

        <Accordion title="POST /api/v1/labels/preview - Generate Preview from JSON">
          <p className="text-gray-600 mb-4">
            Generate a single-label PNG or PDF preview from JSON template data (same payload shape as <code className="bg-gray-100 px-1 rounded">POST /labels/zpl</code>).
            Returns binary image data directly.
          </p>
          <p className="text-gray-600 mb-4">
            For multi-label PDFs from JSON, use <code className="bg-gray-100 px-1 rounded">POST /labels/zpl/batch</code> with <code className="bg-gray-100 px-1 rounded">Accept: application/pdf</code> instead.
            For PDFs from an existing ZPL file, use <code className="bg-gray-100 px-1 rounded">POST /labels/zpl/preview</code>.
          </p>
          <p className="text-amber-600 text-sm">⚠️ Intended for testing and portal previews — not recommended for high-volume production download workflows.</p>
        </Accordion>

        <Accordion title="POST /api/v1/labels/xml - Generate Label from XML/EDI Data">
          <p className="text-gray-600 mb-4">
            Submit XML payload data in RSX format and automatically map it to a label template.
            Useful for EDI integrations that already have data in XML format.
          </p>
          <h4 className="font-medium text-gray-900 mb-2">Request</h4>
          <CodeBlock code={`POST /api/v1/labels/xml
Content-Type: application/xml
X-API-Key: YOUR_API_KEY

<ShippingLabels xmlns="urn:edi-partners:label:rsx">
  <ShippingLabel>
    <Header>
      <ShipFrom>
        <Name>ABC Distribution</Name>
        <Address1>123 Warehouse Blvd</Address1>
        <City>Austin</City>
        <State>TX</State>
        <PostalCode>78701</PostalCode>
      </ShipFrom>
      <ShipTo>
        <Name>Store #17</Name>
        <Address1>456 Oak Ave</Address1>
        <City>Houston</City>
        <State>TX</State>
        <PostalCode>77001</PostalCode>
      </ShipTo>
      <PurchaseOrderNumber>PO123456</PurchaseOrderNumber>
      <SSCC>007468510001263940</SSCC>
    </Header>
  </ShippingLabel>
</ShippingLabels>`} />
          <h4 className="font-medium text-gray-900 mt-4 mb-2">Query Parameters</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Param</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Required</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr><td className="px-4 py-2 font-mono text-sm">templateKey</td><td className="px-4 py-2">Yes</td><td className="px-4 py-2">Template to render</td></tr>
                <tr><td className="px-4 py-2 font-mono text-sm">rootElement</td><td className="px-4 py-2">No</td><td className="px-4 py-2">XPath-like root element to extract from (e.g., <code className="bg-gray-100 px-1 rounded">LabelRequest.Header</code>)</td></tr>
              </tbody>
            </table>
          </div>
        </Accordion>

        <Accordion title="GET /api/v1/ops/templates/:key/schema - Get Template JSON Schema">
          <p className="text-gray-600 mb-4">
            Returns a JSON Schema describing the required data payload for a template.
            Generated from the template&apos;s Handlebars fields and field definitions.
          </p>
          <CodeBlock code={`GET /api/v1/ops/templates/walmart--gs1-shipping-4x6/schema
X-API-Key: YOUR_API_KEY

// Response:
{
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "$id": "walmart--gs1-shipping-4x6",
  "title": "gs1-shipping-4x6",
  "type": "object",
  "properties": {
    "shipTo": {
      "type": "object",
      "properties": {
        "name": { "type": "string" },
        "postalCode": { "type": "string" }
      }
    },
    "sscc": { "type": "string", "pattern": "^\\\\d{17,18}$" },
    "poNumber": { "type": "string" }
  },
  "required": ["shipTo", "sscc", "poNumber"]
}`} />
        </Accordion>

        <Accordion title="GET /api/v1/usage - Get Usage Statistics">
          <p className="text-gray-600 mb-4">Get your API usage statistics.</p>
          <CodeBlock code={`GET /api/v1/usage?startDate=2026-01-01&endDate=2026-01-31`} />
        </Accordion>
      </section>

      {/* Data Fields */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <span>📝</span>
          Data Field Reference
        </h2>

        <Accordion title="Ship From / Ship To Fields" defaultOpen>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Field</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Required</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr><td className="px-4 py-2 font-mono text-sm">name</td><td className="px-4 py-2">string</td><td className="px-4 py-2">Yes</td><td className="px-4 py-2">Company/store name</td></tr>
                <tr><td className="px-4 py-2 font-mono text-sm">address1</td><td className="px-4 py-2">string</td><td className="px-4 py-2">Yes</td><td className="px-4 py-2">Street address line 1</td></tr>
                <tr><td className="px-4 py-2 font-mono text-sm">address2</td><td className="px-4 py-2">string</td><td className="px-4 py-2">No</td><td className="px-4 py-2">Street address line 2</td></tr>
                <tr><td className="px-4 py-2 font-mono text-sm">city</td><td className="px-4 py-2">string</td><td className="px-4 py-2">Yes</td><td className="px-4 py-2">City</td></tr>
                <tr><td className="px-4 py-2 font-mono text-sm">state</td><td className="px-4 py-2">string</td><td className="px-4 py-2">Yes</td><td className="px-4 py-2">State/province code</td></tr>
                <tr><td className="px-4 py-2 font-mono text-sm">postalCode</td><td className="px-4 py-2">string</td><td className="px-4 py-2">Yes</td><td className="px-4 py-2">ZIP/postal code</td></tr>
                <tr><td className="px-4 py-2 font-mono text-sm">country</td><td className="px-4 py-2">string</td><td className="px-4 py-2">No</td><td className="px-4 py-2">Country code (default: US)</td></tr>
                <tr><td className="px-4 py-2 font-mono text-sm">locationNumber</td><td className="px-4 py-2">string</td><td className="px-4 py-2">No</td><td className="px-4 py-2">Store/DC location number</td></tr>
              </tbody>
            </table>
          </div>
        </Accordion>

        <Accordion title="Common Fields">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Field</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr><td className="px-4 py-2 font-mono text-sm">poNumber</td><td className="px-4 py-2">string</td><td className="px-4 py-2">Purchase order number</td></tr>
                <tr><td className="px-4 py-2 font-mono text-sm">sscc</td><td className="px-4 py-2">string</td><td className="px-4 py-2">18-digit Serial Shipping Container Code</td></tr>
                <tr><td className="px-4 py-2 font-mono text-sm">itemNumber</td><td className="px-4 py-2">string</td><td className="px-4 py-2">Buyer's item/SKU number</td></tr>
                <tr><td className="px-4 py-2 font-mono text-sm">itemDescription</td><td className="px-4 py-2">string</td><td className="px-4 py-2">Item description</td></tr>
                <tr><td className="px-4 py-2 font-mono text-sm">upc</td><td className="px-4 py-2">string</td><td className="px-4 py-2">12-digit UPC-A code</td></tr>
                <tr><td className="px-4 py-2 font-mono text-sm">cartonNumber</td><td className="px-4 py-2">number</td><td className="px-4 py-2">Carton sequence number</td></tr>
                <tr><td className="px-4 py-2 font-mono text-sm">cartonTotal</td><td className="px-4 py-2">number</td><td className="px-4 py-2">Total cartons in shipment</td></tr>
                <tr><td className="px-4 py-2 font-mono text-sm">cartonQty</td><td className="px-4 py-2">number</td><td className="px-4 py-2">Quantity in carton</td></tr>
                <tr><td className="px-4 py-2 font-mono text-sm">comment1&ndash;comment5</td><td className="px-4 py-2">string</td><td className="px-4 py-2">Free-text comment / flexfield lines (up to 5). Access via <code className="bg-gray-100 px-1 rounded">{`{{comment 1}}`}</code> helper or <code className="bg-gray-100 px-1 rounded">{`{{commentsBlock}}`}</code> for auto-rendered block.</td></tr>
                <tr><td className="px-4 py-2 font-mono text-sm">flexField1&ndash;flexField3</td><td className="px-4 py-2">string</td><td className="px-4 py-2">Additional flex fields (populated from XML FlexField elements)</td></tr>
              </tbody>
            </table>
          </div>
        </Accordion>
      </section>

      {/* Barcodes */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <span>📦</span>
          Barcode Formats
        </h2>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2">SSCC-18</h3>
            <p className="text-gray-600 text-sm mb-2">Serial Shipping Container Code</p>
            <code className="block bg-gray-100 p-2 rounded text-sm">
              0 0746851 000126394 0
            </code>
            <ul className="mt-2 text-xs text-gray-500 space-y-1">
              <li>• Extension digit (1)</li>
              <li>• Company prefix (7-10)</li>
              <li>• Serial reference (6-9)</li>
              <li>• Check digit (1)</li>
            </ul>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Postal Code (420)</h3>
            <p className="text-gray-600 text-sm mb-2">GS1-128 Ship-to Postal Code</p>
            <code className="block bg-gray-100 p-2 rounded text-sm">
              (420) 78648
            </code>
            <p className="mt-2 text-xs text-gray-500">
              Application Identifier 420 + destination postal code
            </p>
          </div>
        </div>
      </section>

      {/* Code Examples */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <span>🚚</span>
          Code Examples
        </h2>

        <Accordion title="JavaScript / Node.js" defaultOpen>
          <CodeBlock code={`const response = await fetch('https://labelapi.edi-partners.com/api/v1/labels/zpl', {
  method: 'POST',
  headers: {
    'X-API-Key': 'YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    templateKey: 'bucees-shipping-4x6',
    data: {
      shipFrom: { name: 'Warehouse', address1: '123 Main St', city: 'Austin', state: 'TX', postalCode: '78701' },
      shipTo: { name: 'Store #1', address1: '456 Oak Ave', city: 'Houston', state: 'TX', postalCode: '77001' },
      poNumber: 'PO123456',
      sscc: '007468510001263940',
      cartonNumber: 1,
      cartonTotal: 1
    }
  })
});

const { zpl } = await response.json();
// Send ZPL to printer...`} />
        </Accordion>

        <Accordion title="Python">
          <CodeBlock code={`import requests

response = requests.post(
    'https://labelapi.edi-partners.com/api/v1/labels/zpl',
    headers={
        'X-API-Key': 'YOUR_API_KEY',
        'Content-Type': 'application/json'
    },
    json={
        'templateKey': 'bucees-shipping-4x6',
        'data': {
            'shipFrom': {'name': 'Warehouse', 'address1': '123 Main St', 'city': 'Austin', 'state': 'TX', 'postalCode': '78701'},
            'shipTo': {'name': 'Store #1', 'address1': '456 Oak Ave', 'city': 'Houston', 'state': 'TX', 'postalCode': '77001'},
            'poNumber': 'PO123456',
            'sscc': '007468510001263940',
            'cartonNumber': 1,
            'cartonTotal': 1
        }
    }
)

zpl = response.json()['zpl']
# Send ZPL to printer...`} />
        </Accordion>

        <Accordion title="C# / .NET">
          <CodeBlock code={`using var client = new HttpClient();
client.DefaultRequestHeaders.Add("X-API-Key", "YOUR_API_KEY");

var request = new {
    templateKey = "bucees-shipping-4x6",
    data = new {
        shipFrom = new { name = "Warehouse", address1 = "123 Main St", city = "Austin", state = "TX", postalCode = "78701" },
        shipTo = new { name = "Store #1", address1 = "456 Oak Ave", city = "Houston", state = "TX", postalCode = "77001" },
        poNumber = "PO123456",
        sscc = "007468510001263940",
        cartonNumber = 1,
        cartonTotal = 1
    }
};

var response = await client.PostAsJsonAsync(
    "https://labelapi.edi-partners.com/api/v1/labels/zpl",
    request
);

var result = await response.Content.ReadFromJsonAsync<LabelResponse>();
// Send result.Zpl to printer...`} />
        </Accordion>

        <Accordion title="Render existing ZPL file to PDF">
          <p className="text-gray-600 mb-4 text-sm">
            Upload a multi-label ZPL file and save the merged PDF response to disk.
          </p>
          <CodeBlock code={`// Node.js — render an existing .zpl file to PDF
import fs from 'fs';

const zpl = fs.readFileSync('labels-289271872.zpl');

const response = await fetch('https://labelapi.edi-partners.com/api/v1/labels/zpl/preview', {
  method: 'POST',
  headers: {
    'X-API-Key': 'YOUR_API_KEY',
    'Content-Type': 'text/plain',
    'Accept': 'application/pdf',
  },
  body: zpl,
});

if (!response.ok) throw new Error(await response.text());

const pdf = Buffer.from(await response.arrayBuffer());
fs.writeFileSync('labels.pdf', pdf);`} />
          <CodeBlock code={`# Python — render existing ZPL to PDF
import requests

with open('labels-289271872.zpl', 'rb') as f:
    zpl = f.read()

response = requests.post(
    'https://labelapi.edi-partners.com/api/v1/labels/zpl/preview',
    headers={
        'X-API-Key': 'YOUR_API_KEY',
        'Content-Type': 'text/plain',
        'Accept': 'application/pdf',
    },
    data=zpl,
)
response.raise_for_status()

with open('labels.pdf', 'wb') as out:
    out.write(response.content)`} />
          <CodeBlock code={`// C# — render existing ZPL to PDF
using var client = new HttpClient();
client.DefaultRequestHeaders.Add("X-API-Key", "YOUR_API_KEY");

var zpl = await File.ReadAllTextAsync("labels-289271872.zpl");
using var content = new StringContent(zpl, System.Text.Encoding.UTF8, "text/plain");
using var request = new HttpRequestMessage(HttpMethod.Post,
    "https://labelapi.edi-partners.com/api/v1/labels/zpl/preview");
request.Content = content;
request.Headers.Accept.ParseAdd("application/pdf");

var response = await client.SendAsync(request);
response.EnsureSuccessStatusCode();
await File.WriteAllBytesAsync("labels.pdf", await response.Content.ReadAsByteArrayAsync());`} />
        </Accordion>
      </section>

      {/* Error Handling */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Error Handling</h2>
        <p className="text-gray-600 mb-4">All errors return a JSON response with this structure:</p>
        <CodeBlock code={`{
  "error": {
    "code": "VALIDATION_ERROR",
    "message": "Validation failed",
    "details": {
      "issues": [
        { "path": ["data", "sscc"], "message": "SSCC must be 18 digits" }
      ]
    },
    "requestId": "abc123-def456"
  }
}`} />

        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">HTTP Status</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr><td className="px-4 py-2 font-mono text-sm">UNAUTHORIZED</td><td className="px-4 py-2">401</td><td className="px-4 py-2">Invalid or missing API key</td></tr>
              <tr><td className="px-4 py-2 font-mono text-sm">FORBIDDEN</td><td className="px-4 py-2">403</td><td className="px-4 py-2">API key doesn't have access to resource</td></tr>
              <tr><td className="px-4 py-2 font-mono text-sm">NOT_FOUND</td><td className="px-4 py-2">404</td><td className="px-4 py-2">Template or resource not found</td></tr>
              <tr><td className="px-4 py-2 font-mono text-sm">VALIDATION_ERROR</td><td className="px-4 py-2">400</td><td className="px-4 py-2">Invalid request data (missing ^XA/^XZ, too many labels, unsupported Accept header)</td></tr>
              <tr><td className="px-4 py-2 font-mono text-sm">FEATURE_DISABLED</td><td className="px-4 py-2">403</td><td className="px-4 py-2">Preview rendering not enabled in this environment</td></tr>
              <tr><td className="px-4 py-2 font-mono text-sm">PREVIEW_UNAVAILABLE</td><td className="px-4 py-2">503</td><td className="px-4 py-2">Label preview renderer temporarily unavailable — retry later</td></tr>
              <tr><td className="px-4 py-2 font-mono text-sm">RATE_LIMITED</td><td className="px-4 py-2">429</td><td className="px-4 py-2">Too many requests</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Support */}
      <section className="bg-gray-50 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Need Help?</h2>
        <p className="text-gray-600 mb-4">
          Contact our support team for assistance with integration or custom template requirements.
        </p>
        <a 
          href="mailto:support@edi-partners.com"
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Contact Support
        </a>
      </section>
    </>
  );
}

// ─── MCP Setup Tab ──────────────────────────────────────

function McpSetupContent({
  CodeBlock,
  Accordion,
}: {
  CodeBlock: React.ComponentType<{ code: string }>;
  Accordion: React.ComponentType<{ title: string; children: React.ReactNode; defaultOpen?: boolean }>;
}) {
  return (
    <div className="space-y-8">
      <section>
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">MCP Knowledge Server</h2>
        <p className="text-gray-600 mb-4">
          The EDI Label Service MCP server gives AI coding assistants access to label template knowledge,
          template selection guidance, and label generation/preview tools. It uses the same{' '}
          <code className="bg-gray-100 px-1 rounded">X-API-Key</code> as the label API — get your key from the{' '}
          <Link to="/api-keys" className="text-primary-600 hover:underline">API Keys</Link> page.
        </p>
        <p className="text-gray-600 text-sm">
          Preview tools (PDF/PNG) require the preview endpoint to be enabled on the label API. Binary responses
          are returned as base64-encoded JSON so agents can decode and save files locally.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Endpoints</h2>
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-700">Environment</th>
                <th className="px-4 py-2 text-left font-medium text-gray-700">MCP Protocol URL</th>
                <th className="px-4 py-2 text-left font-medium text-gray-700">Health Check</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-2">Production</td>
                <td className="px-4 py-2 font-mono text-xs">https://func-edilabel-mcp-prod.azurewebsites.net/mcp/v1/mcp</td>
                <td className="px-4 py-2 font-mono text-xs">GET .../mcp/v1/health</td>
              </tr>
              <tr>
                <td className="px-4 py-2">Development</td>
                <td className="px-4 py-2 font-mono text-xs">https://func-edilabel-mcp-dev.azurewebsites.net/mcp/v1/mcp</td>
                <td className="px-4 py-2 font-mono text-xs">GET .../mcp/v1/health</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <Accordion title="Claude Code" defaultOpen>
        <p className="text-sm text-gray-600 mb-4">
          Add a project-scoped MCP server in <code className="bg-gray-100 px-1 rounded">.mcp.json</code> at your
          project root, or use the CLI to register the server globally.
        </p>
        <h3 className="font-medium text-gray-900 mb-2">Project config (.mcp.json)</h3>
        <CodeBlock code={`{
  "mcpServers": {
    "edi-label-knowledge": {
      "type": "http",
      "url": "https://func-edilabel-mcp-prod.azurewebsites.net/mcp/v1/mcp",
      "headers": {
        "X-API-Key": "\${EDI_LABEL_API_KEY}"
      }
    }
  }
}`} />
        <p className="text-sm text-gray-600 mt-4 mb-2">
          Set <code className="bg-gray-100 px-1 rounded">EDI_LABEL_API_KEY</code> in your environment before starting Claude Code.
        </p>
        <h3 className="font-medium text-gray-900 mb-2">CLI alternative</h3>
        <CodeBlock code={`claude mcp add --transport http edi-label-knowledge \\
  --header "X-API-Key: $EDI_LABEL_API_KEY" \\
  https://func-edilabel-mcp-prod.azurewebsites.net/mcp/v1/mcp`} />
      </Accordion>

      <Accordion title="Codex">
        <p className="text-sm text-gray-600 mb-4">
          Configure Codex in <code className="bg-gray-100 px-1 rounded">~/.codex/config.toml</code> for all projects,
          or in <code className="bg-gray-100 px-1 rounded">.codex/config.toml</code> inside a trusted project directory.
        </p>
        <CodeBlock code={`[mcp_servers.edi-label-knowledge]
url = "https://func-edilabel-mcp-prod.azurewebsites.net/mcp/v1/mcp"
http_headers = { "X-API-Key" = "\${EDI_LABEL_API_KEY}" }
tool_timeout_sec = 120`} />
        <p className="text-sm text-gray-600 mt-4">
          Use a longer timeout for batch PDF and multi-label ZPL preview tools. Project-level config requires
          trusting the project directory in Codex.
        </p>
      </Accordion>

      <Accordion title="VS Code Copilot">
        <p className="text-sm text-gray-600 mb-4">
          Add an HTTP MCP server in <code className="bg-gray-100 px-1 rounded">.vscode/mcp.json</code> with the same
          URL and <code className="bg-gray-100 px-1 rounded">X-API-Key</code> header. See the repository integration
          guide for full VS Code and ChatGPT Actions setup.
        </p>
      </Accordion>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Available Tools (17)</h2>
        <div className="overflow-x-auto border border-gray-200 rounded-lg mb-6">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-700">Category</th>
                <th className="px-4 py-2 text-left font-medium text-gray-700">Tool</th>
                <th className="px-4 py-2 text-left font-medium text-gray-700">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {[
                ['Knowledge', 'get-retailer-overview', 'Retailer overview with all templates'],
                ['Knowledge', 'query-templates', 'Query templates by retailer, type, or program'],
                ['Knowledge', 'explain-template', 'Detailed template explanation'],
                ['Knowledge', 'get-template-requirements', 'Required fields, dimensions, barcode specs'],
                ['Knowledge', 'get-example-payload', 'Example JSON payload for testing'],
                ['Knowledge', 'get-decision-matrix', 'Template selection decision matrix'],
                ['Knowledge', 'compare-templates', 'Side-by-side template comparison'],
                ['Knowledge', 'select-label', 'Recommend best template for shipment facts'],
                ['Knowledge', 'validate-label-choice', 'Validate a template choice'],
                ['Label output', 'generate-test-label', 'Generate ZPL or PDF/PNG preview for one template'],
                ['Label output', 'generate-batch-labels', 'Sync batch ≤100 — JSON, ZPL, PDF, or PNG zip'],
                ['Label output', 'generate-large-batch-labels', 'Async bulk 1–10k; labelsPerRoll (default 1000); artifacts zpl + zpl-zip + pdf-zip'],
                ['Label output', 'create-label-job', 'Low-level create (+ labelsPerRoll, webhook, email); optional auto-upload'],
                ['Label output', 'commit-label-job', 'Commit uploaded manifest and start pipeline'],
                ['Label output', 'get-label-job-status', 'Poll status; refresh SAS for zpl / zpl-zip / pdf-zip'],
                ['Label output', 'cancel-label-job', 'Cancel an in-flight async job'],
                ['Label output', 'render-zpl-preview', 'Render existing ZPL to PDF or PNG zip'],
              ].map(([category, tool, description]) => (
                <tr key={tool}>
                  <td className="px-4 py-2 text-gray-600">{category}</td>
                  <td className="px-4 py-2 font-mono text-xs">{tool}</td>
                  <td className="px-4 py-2">{description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Example Prompts</h2>
        <ul className="space-y-2 text-sm text-gray-700">
          <li>What templates does Walmart have?</li>
          <li>Which template should I use for a Walmart DSD shipment?</li>
          <li>Generate a PDF preview of the Walmart shipping label with this order data.</li>
          <li>Render this ZPL file as a merged PDF with 78 labels.</li>
          <li>Generate a batch PDF for these 10 label requests.</li>
          <li>Submit an async job for 1000 shipping labels with labelsPerRoll 1000 and poll until zpl, zpl-zip, and pdf-zip are ready.</li>
          <li>Create a bulk job for this ASN wave (2000 labels) with ZPL only for thermal print, rolls of 1000.</li>
          <li>Run generate-large-batch-labels for 5000 labels with outputs zpl and pdf; use default roll size 1000.</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Bulk jobs via MCP (agents)</h2>
        <p className="text-sm text-gray-600 mb-3">
          Use <code className="bg-gray-100 px-1 rounded">generate-large-batch-labels</code> when count &gt; 100.
          Hard max <strong>10,000</strong> labels per job.
        </p>
        <ul className="text-sm text-gray-700 list-disc list-inside space-y-1 mb-3">
          <li><strong>labelsPerRoll</strong> (optional, default <strong>1000</strong>) — warehouse print unit for PDF and ZPL roll files</li>
          <li>Server render part size is ops-configured (~<strong>250</strong>); agents should not set it</li>
          <li>Artifacts: mono <code className="bg-gray-100 px-1 rounded">zpl</code>, <code className="bg-gray-100 px-1 rounded">zpl-zip</code> (rolls), <code className="bg-gray-100 px-1 rounded">pdf-zip</code> (rolls)</li>
          <li>Optional <code className="bg-gray-100 px-1 rounded">webhookUrl</code> (https) and <code className="bg-gray-100 px-1 rounded">notifyEmail</code></li>
        </ul>
        <CodeBlock code={`// tools/call generate-large-batch-labels
{
  "labels": [ { "templateKey": "retail--natural-grocer--shipping-4x6", "data": { /* … */ } } ],
  "outputs": ["zpl", "pdf"],
  "labelsPerRoll": 1000,
  "wait": true,
  "maxWaitMs": 300000
}
// Then download artifacts[].url for kinds zpl | zpl-zip | pdf-zip`} />
      </section>

      <section className="bg-gray-50 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">More Integration Options</h2>
        <p className="text-gray-600 text-sm">
          For VS Code Copilot, ChatGPT Custom GPT Actions, raw JSON-RPC examples, and the full OpenAPI schema,
          see <code className="bg-gray-100 px-1 rounded">docs/mcp/INTEGRATION_GUIDE.md</code> in the repository.
        </p>
      </section>
    </div>
  );
}
